import { quarantineCustomerOwnedSessionStorage } from "@/app/booking/core/customerOwnedSessionStorageBarrier";
import {
  captureCustomerOwnedSessionStorageMutationProof,
  commitCustomerOwnedSessionStorageMutation,
  customerOwnedSessionStorageMutationProofIsCurrent,
  readCustomerOwnedSessionStorageRawValueFromCapturedReader,
  readCustomerOwnedSessionStorageRawValueStable,
  type CustomerOwnedSessionStorageMutationProof,
} from "@/app/booking/core/customerOwnedSessionStorageMutationCoordinator";
import type { SpecialistLocationGatewayCommittedNavigationLineageV1 } from "@/app/specialists/locationGateway/contract";
import {
  beginSpecialistLocationGatewayTerminalWrite,
  completeSpecialistLocationGatewayTerminalWrite,
  specialistLocationGatewayTerminalWriteIsCurrent,
  specialistLocationGatewayTerminalWriteLeaseIsCurrent,
  type SpecialistLocationGatewayTerminalWriteLease,
} from "@/app/specialists/locationGateway/terminalWriteCoordinator";

import {
  validateAppointmentWaitlistReceipt,
  type AppointmentWaitlistReceiptV3,
} from "./appointmentWaitlistReceipt";
import type { AppointmentWaitlistSubmissionAttemptV1 } from "./appointmentWaitlistSubmissionAttempt";
import {
  appointmentWaitlistReceiptMatchesAcceptedAttempt,
  appointmentWaitlistGatewayLineagesAreExact,
  appointmentWaitlistTerminalAttemptAggregatesAreExact,
  cloneAppointmentWaitlistTerminalAttempt,
  createAppointmentWaitlistTerminalAttemptAggregate,
  isAppointmentWaitlistTerminalAttemptEvaluatedEpoch,
  isLegalAppointmentWaitlistTerminalAttemptStateTransition,
  validateAppointmentWaitlistTerminalAttemptAggregate,
  type AppointmentWaitlistTerminalAttemptAggregateV1,
  type AppointmentWaitlistTerminalAttemptOriginRequirement,
} from "./appointmentWaitlistTerminalAttemptAggregate";
import { migrateRetiredGroupRowsFromSharedAppointmentWaitlistStorage } from "./retiredGroupAppointmentWaitlistStorageMigration";
import { APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_MAX_SERIALIZED_CHARACTERS } from "./appointmentWaitlistStorageLimits";

export const APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_KEY =
  "volera:appointment-waitlist-terminal-attempt-aggregates:v2";
export const APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_MAX_ENTRIES = 8;
export const APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_MAX_AGE_MS =
  Number.MAX_SAFE_INTEGER;
const APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_STORE_KIND =
  "appointment-waitlist-terminal-attempt-aggregates-v2";

function isIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= 512 &&
    !/[\u0000-\u001f\u007f]/u.test(value)
  );
}

function isOriginRequirement(
  value: unknown,
): value is AppointmentWaitlistTerminalAttemptOriginRequirement {
  return value === "legacy-non-gateway" || value === "gateway";
}

interface PersistedAggregateEntry {
  readonly draftId: string;
  readonly savedAtEpochMs: number;
  readonly draft: AppointmentWaitlistTerminalAttemptAggregateV1;
}

function parsePersistedAggregateEntries(
  rawValue: string | null,
  evaluatedAtEpochMs: number,
) {
  if (rawValue === null) return [] as readonly PersistedAggregateEntry[];
  if (
    rawValue.length >
      APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_MAX_SERIALIZED_CHARACTERS ||
    !isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(evaluatedAtEpochMs)
  ) {
    return null;
  }
  try {
    const parsed = JSON.parse(rawValue) as unknown;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      Array.isArray(parsed) ||
      Object.keys(parsed).length !== 3 ||
      !("storeVersion" in parsed) ||
      parsed.storeVersion !== 2 ||
      !("storeKind" in parsed) ||
      parsed.storeKind !==
        APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_STORE_KIND ||
      !("entries" in parsed) ||
      !Array.isArray(parsed.entries) ||
      parsed.entries.length >
        APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_MAX_ENTRIES
    ) {
      return null;
    }
    const seen = new Set<string>();
    const entries: PersistedAggregateEntry[] = [];
    for (const value of parsed.entries) {
      if (
        !value ||
        typeof value !== "object" ||
        Array.isArray(value) ||
        Object.keys(value).length !== 3 ||
        !("draftId" in value) ||
        !isIdentifier(value.draftId) ||
        seen.has(value.draftId) ||
        !("savedAtEpochMs" in value) ||
        !isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(
          value.savedAtEpochMs,
        ) ||
        value.savedAtEpochMs > evaluatedAtEpochMs + 5 * 60_000 ||
        !("draft" in value)
      ) {
        return null;
      }
      const validation =
        validateAppointmentWaitlistTerminalAttemptAggregate(value.draft);
      if (
        !validation.valid ||
        validation.aggregate.draftId !== value.draftId ||
        (validation.aggregate.resultsEntryProof !== null &&
          validation.aggregate.resultsEntryProof.capturedAtEpochMs >
            value.savedAtEpochMs) ||
        JSON.stringify(validation.aggregate) !== JSON.stringify(value.draft)
      ) {
        return null;
      }
      seen.add(value.draftId);
      entries.push({
        draftId: value.draftId,
        savedAtEpochMs: value.savedAtEpochMs,
        draft: validation.aggregate,
      });
    }
    return Object.freeze(entries);
  } catch {
    return null;
  }
}

function serializePersistedAggregateEntries(
  entries: readonly PersistedAggregateEntry[],
) {
  return entries.length === 0
    ? null
    : JSON.stringify({
        storeVersion: 2,
        storeKind:
          APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_STORE_KIND,
        entries,
      });
}

interface CanonicalAggregateRawSnapshot {
  readonly storage: Storage;
  readonly rawValue: string | null;
  readonly entries: readonly PersistedAggregateEntry[];
  readonly mutationProof: CustomerOwnedSessionStorageMutationProof;
  readonly evaluatedAtEpochMs: number;
}

function readSafeAppointmentWaitlistTerminalAttemptEpoch() {
  try {
    const value = Date.now();
    return isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(value)
      ? value
      : null;
  } catch {
    return null;
  }
}

function captureCanonicalAggregateRawStable(evaluatedAtEpochMs: number) {
  if (
    !isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(evaluatedAtEpochMs)
  ) {
    return null;
  }
  if (
    !migrateRetiredGroupRowsFromSharedAppointmentWaitlistStorage(
      evaluatedAtEpochMs,
    )
  ) {
    return null;
  }
  const mutationProof = captureCustomerOwnedSessionStorageMutationProof();
  try {
    const storage = window.sessionStorage;
    const raw = readCustomerOwnedSessionStorageRawValueStable({
      storage,
      storageKey:
        APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_KEY,
    });
    const entries =
      raw.status === "ready"
        ? parsePersistedAggregateEntries(raw.rawValue, evaluatedAtEpochMs)
        : null;
    if (
      raw.status !== "ready" ||
      !entries ||
      !customerOwnedSessionStorageMutationProofIsCurrent(mutationProof)
    ) {
      quarantineCustomerOwnedSessionStorage();
      return null;
    }
    return Object.freeze({
      storage,
      rawValue: raw.rawValue,
      entries,
      mutationProof,
      evaluatedAtEpochMs,
    }) satisfies CanonicalAggregateRawSnapshot;
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return null;
  }
}

function captureCanonicalAggregateRawFromCapturedReader(input: {
  storage: Storage;
  evaluatedAtEpochMs: number;
}) {
  const mutationProof = captureCustomerOwnedSessionStorageMutationProof();
  const raw = readCustomerOwnedSessionStorageRawValueFromCapturedReader({
    storage: input.storage,
    storageKey:
      APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_KEY,
  });
  const entries =
    raw.status === "ready"
      ? parsePersistedAggregateEntries(raw.rawValue, input.evaluatedAtEpochMs)
      : null;
  return raw.status === "ready" &&
    entries &&
    customerOwnedSessionStorageMutationProofIsCurrent(mutationProof)
    ? (Object.freeze({
        storage: input.storage,
        rawValue: raw.rawValue,
        entries,
        mutationProof,
        evaluatedAtEpochMs: input.evaluatedAtEpochMs,
      }) satisfies CanonicalAggregateRawSnapshot)
    : null;
}

function canonicalSnapshotRemainsExact(input: {
  snapshot: CanonicalAggregateRawSnapshot;
  lease?: SpecialistLocationGatewayTerminalWriteLease;
}) {
  if (
    !customerOwnedSessionStorageMutationProofIsCurrent(
      input.snapshot.mutationProof,
    ) ||
    (input.lease &&
      !specialistLocationGatewayTerminalWriteLeaseIsCurrent(input.lease))
  ) {
    return false;
  }
  const raw = readCustomerOwnedSessionStorageRawValueFromCapturedReader({
    storage: input.snapshot.storage,
    storageKey:
      APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_KEY,
  });
  return (
    raw.status === "ready" &&
    raw.rawValue === input.snapshot.rawValue &&
    customerOwnedSessionStorageMutationProofIsCurrent(
      input.snapshot.mutationProof,
    ) &&
    (!input.lease ||
      specialistLocationGatewayTerminalWriteLeaseIsCurrent(input.lease))
  );
}

function ownerAndCanonicalSnapshotRemainExact(input: {
  snapshot: CanonicalAggregateRawSnapshot;
  lease: SpecialistLocationGatewayTerminalWriteLease;
  isCurrentOwner: () => boolean;
  isPureCurrentOwner: () => boolean;
}) {
  if (
    !specialistLocationGatewayTerminalWriteIsCurrent(
      input.lease,
      input.isCurrentOwner,
    )
  ) {
    return false;
  }
  try {
    return (
      canonicalSnapshotRemainsExact({
        snapshot: input.snapshot,
        lease: input.lease,
      }) &&
      input.isPureCurrentOwner() === true &&
      canonicalSnapshotRemainsExact({
        snapshot: input.snapshot,
        lease: input.lease,
      })
    );
  } catch {
    return false;
  }
}

function entryForDraft(
  snapshot: CanonicalAggregateRawSnapshot,
  draftId: string,
) {
  const matches = snapshot.entries.filter((entry) => entry.draftId === draftId);
  return matches.length === 1 ? matches[0]! : null;
}

function commitCanonicalAggregateEntries(input: {
  snapshot: CanonicalAggregateRawSnapshot;
  nextEntries: readonly PersistedAggregateEntry[];
  staleEntries: readonly PersistedAggregateEntry[];
  lease: SpecialistLocationGatewayTerminalWriteLease;
}) {
  if (
    !canonicalSnapshotRemainsExact({
      snapshot: input.snapshot,
      lease: input.lease,
    })
  ) {
    return { status: "failed" as const };
  }
  const nextRawValue = serializePersistedAggregateEntries(input.nextEntries);
  const mutation = commitCustomerOwnedSessionStorageMutation({
    storage: input.snapshot.storage,
    storageKey:
      APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_KEY,
    nextRawValue,
    expectedCurrentRawValue: input.snapshot.rawValue,
    mutationProof: input.snapshot.mutationProof,
    mutationIsCurrent: () =>
      specialistLocationGatewayTerminalWriteLeaseIsCurrent(input.lease),
    staleCleanupRawValue: serializePersistedAggregateEntries(
      input.staleEntries,
    ),
  });
  if (mutation.status !== "committed" && mutation.status !== "superseded") {
    return { status: "failed" as const };
  }
  const final = captureCanonicalAggregateRawFromCapturedReader({
    storage: input.snapshot.storage,
    evaluatedAtEpochMs: input.snapshot.evaluatedAtEpochMs,
  });
  if (
    !final ||
    !specialistLocationGatewayTerminalWriteLeaseIsCurrent(input.lease)
  ) {
    return { status: "failed" as const };
  }
  return final.rawValue === nextRawValue
    ? { status: "committed" as const, snapshot: final }
    : { status: "superseded" as const, snapshot: final };
}

function aggregateMatchesAttempt(input: {
  aggregate: AppointmentWaitlistTerminalAttemptAggregateV1;
  attempt: AppointmentWaitlistSubmissionAttemptV1;
  originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  expectedGatewayLineage: unknown;
}) {
  const lineageMatches =
    input.originRequirement === "legacy-non-gateway"
      ? input.expectedGatewayLineage === null &&
        input.aggregate.gatewayLineage === null
      : appointmentWaitlistGatewayLineagesAreExact(
          input.aggregate.gatewayLineage,
          input.expectedGatewayLineage,
        );
  return (
    input.aggregate.originRequirement === input.originRequirement &&
    lineageMatches &&
    appointmentWaitlistTerminalAttemptAggregatesAreExact(
      input.aggregate.attempt,
      input.attempt,
    )
  );
}

function pureOwnerIsCurrentNoThrow(isPureCurrentOwner: () => boolean) {
  try {
    return isPureCurrentOwner() === true;
  } catch {
    return false;
  }
}

function finalSnapshotEntryIsExact(input: {
  snapshot: CanonicalAggregateRawSnapshot;
  expected: AppointmentWaitlistTerminalAttemptAggregateV1;
  lease: SpecialistLocationGatewayTerminalWriteLease;
  isPureCurrentOwner: () => boolean;
}) {
  const entry = entryForDraft(input.snapshot, input.expected.draftId);
  return Boolean(
    entry &&
      appointmentWaitlistTerminalAttemptAggregatesAreExact(
        entry.draft,
        input.expected,
      ) &&
      canonicalSnapshotRemainsExact({
        snapshot: input.snapshot,
        lease: input.lease,
      }) &&
      pureOwnerIsCurrentNoThrow(input.isPureCurrentOwner) &&
      canonicalSnapshotRemainsExact({
        snapshot: input.snapshot,
        lease: input.lease,
      }),
  );
}

function closePostWriteCanonicalAuthority(input: {
  snapshot: CanonicalAggregateRawSnapshot;
  lease: SpecialistLocationGatewayTerminalWriteLease;
  isCurrentOwner: () => boolean;
  isPureCurrentOwner: () => boolean;
  snapshotMatches: () => boolean;
}) {
  let ownerCurrent = false;
  try {
    ownerCurrent = specialistLocationGatewayTerminalWriteIsCurrent(
      input.lease,
      input.isCurrentOwner,
    );
  } catch {
    ownerCurrent = false;
  }
  // The owner callback is the final external frontier. From here onward only
  // the captured canonical reader plus pure lease/generation checks may run.
  const canonicalBeforePure = canonicalSnapshotRemainsExact({
    snapshot: input.snapshot,
    lease: input.lease,
  });
  const pureOwnerCurrent = pureOwnerIsCurrentNoThrow(
    input.isPureCurrentOwner,
  );
  const canonicalAfterPure = canonicalSnapshotRemainsExact({
    snapshot: input.snapshot,
    lease: input.lease,
  });
  let snapshotMatches = false;
  try {
    snapshotMatches = input.snapshotMatches() === true;
  } catch {
    snapshotMatches = false;
  }
  return {
    canonicalExact:
      canonicalBeforePure &&
      pureOwnerCurrent &&
      canonicalAfterPure &&
      snapshotMatches,
    ownerCurrent,
  } as const;
}

function compensatePostWriteCanonicalPublication(input: {
  snapshot: CanonicalAggregateRawSnapshot;
  staleEntries: readonly PersistedAggregateEntry[];
  lease: SpecialistLocationGatewayTerminalWriteLease;
}) {
  const compensated = commitCanonicalAggregateEntries({
    snapshot: input.snapshot,
    nextEntries: input.staleEntries,
    staleEntries: input.staleEntries,
    lease: input.lease,
  });
  const compensatedRaw = serializePersistedAggregateEntries(
    input.staleEntries,
  );
  const compensatedExactly =
    compensated.status !== "failed" &&
    compensated.snapshot.rawValue === compensatedRaw &&
    specialistLocationGatewayTerminalWriteLeaseIsCurrent(input.lease) &&
    canonicalSnapshotRemainsExact({
      snapshot: compensated.snapshot,
      lease: input.lease,
    });
  if (!compensatedExactly) quarantineCustomerOwnedSessionStorage();
  return compensatedExactly;
}

function acknowledgedCapacityEntry(input: {
  snapshot: CanonicalAggregateRawSnapshot;
  expectedCustomerAccountId: string;
}) {
  return input.snapshot.entries
    .filter(
      ({ draft }) =>
        draft.customerAccountId === input.expectedCustomerAccountId &&
        draft.receipt !== null &&
        draft.acknowledgedAtEpochMs !== null,
    )
    .sort((left, right) => {
      const epochDelta =
        left.draft.acknowledgedAtEpochMs! -
        right.draft.acknowledgedAtEpochMs!;
      return epochDelta !== 0
        ? epochDelta
        : left.draftId.localeCompare(right.draftId);
    })[0] ?? null;
}

export type CreateAppointmentWaitlistTerminalAttemptAggregateStrictResult =
  | {
      status: "created" | "already-created";
      aggregate: AppointmentWaitlistTerminalAttemptAggregateV1;
      ownerCurrent: true;
    }
  | { status: "refused" };

/** Persists the exact pending Attempt and immutable origin before provider entry. */
export function createAppointmentWaitlistTerminalAttemptAggregateStrict(input: {
  attempt: unknown;
  originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  gatewayLineage: unknown;
  resultsEntryProof?: unknown;
  evaluatedAtEpochMs: number;
  isCurrentOwner: () => boolean;
  /** Callback-free handoff/component generation proof. */
  isPureCurrentOwner: () => boolean;
}): CreateAppointmentWaitlistTerminalAttemptAggregateStrictResult {
  let candidate: AppointmentWaitlistTerminalAttemptAggregateV1 | null;
  let evaluatedAtEpochMs: number;
  let isCurrentOwner: () => boolean;
  let isPureCurrentOwner: () => boolean;
  try {
    evaluatedAtEpochMs = input.evaluatedAtEpochMs;
    isCurrentOwner = input.isCurrentOwner;
    isPureCurrentOwner = input.isPureCurrentOwner;
    candidate = createAppointmentWaitlistTerminalAttemptAggregate({
      attempt: input.attempt,
      originRequirement: input.originRequirement,
      gatewayLineage: input.gatewayLineage,
      resultsEntryProof: input.resultsEntryProof ?? null,
      evaluatedAtEpochMs,
    });
  } catch {
    return { status: "refused" };
  }
  if (
    !candidate ||
    !isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(evaluatedAtEpochMs) ||
    typeof isCurrentOwner !== "function" ||
    typeof isPureCurrentOwner !== "function"
  ) {
    return { status: "refused" };
  }
  const initial = captureCanonicalAggregateRawStable(evaluatedAtEpochMs);
  if (!initial) return { status: "refused" };
  const initialEntry = entryForDraft(initial, candidate.draftId);
  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify(["waitlist-terminal-attempt-create", candidate.draftId]),
    isCurrentOwner,
  );
  if (!lease) return { status: "refused" };
  try {
    if (
      !ownerAndCanonicalSnapshotRemainExact({
        snapshot: initial,
        lease,
        isCurrentOwner,
        isPureCurrentOwner,
      })
    ) {
      return { status: "refused" };
    }
    if (initialEntry) {
      if (
        !appointmentWaitlistTerminalAttemptAggregatesAreExact(
          initialEntry.draft,
          candidate,
        )
      ) {
        return { status: "refused" };
      }
      return finalSnapshotEntryIsExact({
        snapshot: initial,
        expected: initialEntry.draft,
        lease,
        isPureCurrentOwner,
      })
        ? {
            status: "already-created",
            aggregate: initialEntry.draft,
            ownerCurrent: true,
          }
        : { status: "refused" };
    }
    const capacityEntry =
      initial.entries.length >=
      APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_MAX_ENTRIES
        ? acknowledgedCapacityEntry({
            snapshot: initial,
            expectedCustomerAccountId: candidate.customerAccountId,
          })
        : null;
    if (
      initial.entries.length >=
        APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_MAX_ENTRIES &&
      !capacityEntry
    ) {
      return { status: "refused" };
    }
    const retained = capacityEntry
      ? initial.entries.filter(
          (entry) => entry.draftId !== capacityEntry.draftId,
        )
      : [...initial.entries];
    const committed = commitCanonicalAggregateEntries({
      snapshot: initial,
      nextEntries: [
        ...retained,
        {
          draftId: candidate.draftId,
          savedAtEpochMs: evaluatedAtEpochMs,
          draft: candidate,
        },
      ],
      // If the lease is lost, the candidate must not survive and an
      // acknowledged capacity victim must not be reclaimed.
      staleEntries: initial.entries,
      lease,
    });
    if (committed.status === "failed") {
      return { status: "refused" };
    }
    const postWrite = closePostWriteCanonicalAuthority({
      snapshot: committed.snapshot,
      lease,
      isCurrentOwner,
      isPureCurrentOwner,
      snapshotMatches: () => {
        const entry = entryForDraft(committed.snapshot, candidate.draftId);
        return Boolean(
          entry &&
            appointmentWaitlistTerminalAttemptAggregatesAreExact(
              entry.draft,
              candidate,
            ) &&
            (capacityEntry === null ||
              entryForDraft(
                committed.snapshot,
                capacityEntry.draftId,
              ) === null),
        );
      },
    });
    if (!postWrite.ownerCurrent || !postWrite.canonicalExact) {
      if (!postWrite.ownerCurrent && postWrite.canonicalExact) {
        compensatePostWriteCanonicalPublication({
          snapshot: committed.snapshot,
          staleEntries: initial.entries,
          lease,
        });
      }
      return { status: "refused" };
    }
    return committed.status === "committed"
      ? { status: "created", aggregate: candidate, ownerCurrent: true }
      : {
          status: "already-created",
          aggregate: candidate,
          ownerCurrent: true,
        };
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}

export type InvokeAppointmentWaitlistTerminalAttemptProviderStrictResult<Result> =
  | {
      status: "invoked";
      authority: AppointmentWaitlistTerminalAttemptOriginRequirement;
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1 | null;
      providerInvocation:
        | { status: "returned"; value: Result }
        | { status: "threw" };
    }
  | { status: "refused" };

/** Provider frontier: only the final exact canonical read grants invocation. */
export function invokeAppointmentWaitlistTerminalAttemptProviderStrict<Result>(input: {
  attempt: unknown;
  expectedCustomerAccountId: string;
  originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  expectedGatewayLineage: unknown;
  evaluatedAtEpochMs: number;
  isCurrentOwner: () => boolean;
  isPureCurrentOwner: () => boolean;
  invokeProvider: () => Result;
}): InvokeAppointmentWaitlistTerminalAttemptProviderStrictResult<Result> {
  let attempt: AppointmentWaitlistSubmissionAttemptV1 | null;
  let expectedCustomerAccountId: string;
  let originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  let expectedGatewayLineage: unknown;
  let evaluatedAtEpochMs: number;
  let isCurrentOwner: () => boolean;
  let isPureCurrentOwner: () => boolean;
  let invokeProvider: () => Result;
  try {
    evaluatedAtEpochMs = input.evaluatedAtEpochMs;
    attempt = cloneAppointmentWaitlistTerminalAttempt(
      input.attempt,
      evaluatedAtEpochMs,
    );
    expectedCustomerAccountId = input.expectedCustomerAccountId;
    originRequirement = input.originRequirement;
    expectedGatewayLineage = input.expectedGatewayLineage;
    isCurrentOwner = input.isCurrentOwner;
    isPureCurrentOwner = input.isPureCurrentOwner;
    invokeProvider = input.invokeProvider;
  } catch {
    return { status: "refused" };
  }
  if (
    !attempt ||
    attempt.state.kind !== "pending-reconciliation" ||
    attempt.customerAccountId !== expectedCustomerAccountId ||
    !isIdentifier(expectedCustomerAccountId) ||
    !isOriginRequirement(originRequirement) ||
    !isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(evaluatedAtEpochMs) ||
    typeof isCurrentOwner !== "function" ||
    typeof isPureCurrentOwner !== "function" ||
    typeof invokeProvider !== "function"
  ) {
    return { status: "refused" };
  }
  const initial = captureCanonicalAggregateRawStable(evaluatedAtEpochMs);
  if (!initial) return { status: "refused" };
  const initialEntry = entryForDraft(initial, attempt.draftId);
  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify(["waitlist-terminal-attempt-provider", attempt.draftId]),
    isCurrentOwner,
  );
  if (!lease) return { status: "refused" };
  try {
    if (
      !initialEntry ||
      !aggregateMatchesAttempt({
        aggregate: initialEntry.draft,
        attempt,
        originRequirement,
        expectedGatewayLineage,
      }) ||
      initialEntry.draft.receipt !== null ||
      !ownerAndCanonicalSnapshotRemainExact({
        snapshot: initial,
        lease,
        isCurrentOwner,
        isPureCurrentOwner,
      })
    ) {
      return { status: "refused" };
    }
    if (
      !finalSnapshotEntryIsExact({
        snapshot: initial,
        expected: initialEntry.draft,
        lease,
        isPureCurrentOwner,
      })
    ) {
      return { status: "refused" };
    }
    try {
      return {
        status: "invoked",
        authority: initialEntry.draft.originRequirement,
        lineage: initialEntry.draft.gatewayLineage,
        providerInvocation: { status: "returned", value: invokeProvider() },
      };
    } catch {
      return {
        status: "invoked",
        authority: initialEntry.draft.originRequirement,
        lineage: initialEntry.draft.gatewayLineage,
        providerInvocation: { status: "threw" },
      };
    }
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}

export type TransitionAppointmentWaitlistTerminalAttemptAggregateStrictResult =
  | {
      status: "transitioned" | "already-transitioned";
      aggregate: AppointmentWaitlistTerminalAttemptAggregateV1;
      ownerCurrent: true;
    }
  | { status: "refused" };

export function transitionAppointmentWaitlistTerminalAttemptAggregateStrict(input: {
  expectedAttempt: unknown;
  nextAttempt: unknown;
  originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  expectedGatewayLineage: unknown;
  evaluatedAtEpochMs: number;
  isCurrentOwner: () => boolean;
  isPureCurrentOwner: () => boolean;
}): TransitionAppointmentWaitlistTerminalAttemptAggregateStrictResult {
  let expected: AppointmentWaitlistSubmissionAttemptV1 | null;
  let next: AppointmentWaitlistSubmissionAttemptV1 | null;
  let originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  let expectedGatewayLineage: unknown;
  let evaluatedAtEpochMs: number;
  let isCurrentOwner: () => boolean;
  let isPureCurrentOwner: () => boolean;
  try {
    evaluatedAtEpochMs = input.evaluatedAtEpochMs;
    expected = cloneAppointmentWaitlistTerminalAttempt(
      input.expectedAttempt,
      evaluatedAtEpochMs,
    );
    next = cloneAppointmentWaitlistTerminalAttempt(
      input.nextAttempt,
      evaluatedAtEpochMs,
    );
    originRequirement = input.originRequirement;
    expectedGatewayLineage = input.expectedGatewayLineage;
    isCurrentOwner = input.isCurrentOwner;
    isPureCurrentOwner = input.isPureCurrentOwner;
  } catch {
    return { status: "refused" };
  }
  if (
    !expected ||
    !next ||
    !isOriginRequirement(originRequirement) ||
    !isLegalAppointmentWaitlistTerminalAttemptStateTransition({
      expected,
      next,
    }) ||
    typeof isCurrentOwner !== "function" ||
    typeof isPureCurrentOwner !== "function"
  ) {
    return { status: "refused" };
  }
  const initial = captureCanonicalAggregateRawStable(evaluatedAtEpochMs);
  if (!initial) return { status: "refused" };
  const initialEntry = entryForDraft(initial, expected.draftId);
  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify(["waitlist-terminal-attempt-transition", expected.draftId]),
    isCurrentOwner,
  );
  if (!lease) return { status: "refused" };
  try {
    if (!initialEntry || initialEntry.draft.receipt !== null) {
      return { status: "refused" };
    }
    if (
      aggregateMatchesAttempt({
        aggregate: initialEntry.draft,
        attempt: next,
        originRequirement,
        expectedGatewayLineage,
      })
    ) {
      return ownerAndCanonicalSnapshotRemainExact({
        snapshot: initial,
        lease,
        isCurrentOwner,
        isPureCurrentOwner,
      }) &&
        finalSnapshotEntryIsExact({
          snapshot: initial,
          expected: initialEntry.draft,
          lease,
          isPureCurrentOwner,
        })
        ? {
            status: "already-transitioned",
            aggregate: initialEntry.draft,
            ownerCurrent: true,
          }
        : { status: "refused" };
    }
    if (
      !aggregateMatchesAttempt({
        aggregate: initialEntry.draft,
        attempt: expected,
        originRequirement,
        expectedGatewayLineage,
      }) ||
      !ownerAndCanonicalSnapshotRemainExact({
        snapshot: initial,
        lease,
        isCurrentOwner,
        isPureCurrentOwner,
      })
    ) {
      return { status: "refused" };
    }
    const nextAggregate: AppointmentWaitlistTerminalAttemptAggregateV1 = {
      ...initialEntry.draft,
      revision: initialEntry.draft.revision + 1,
      attempt: next,
    };
    const staleEntries = initial.entries.filter(
      (entry) => entry.draftId !== nextAggregate.draftId,
    );
    const committed = commitCanonicalAggregateEntries({
      snapshot: initial,
      nextEntries: initial.entries.map((entry) =>
        entry.draftId === nextAggregate.draftId
          ? {
              draftId: nextAggregate.draftId,
              savedAtEpochMs: evaluatedAtEpochMs,
              draft: nextAggregate,
            }
          : entry,
      ),
      staleEntries,
      lease,
    });
    if (committed.status === "failed") {
      return { status: "refused" };
    }
    const postWrite = closePostWriteCanonicalAuthority({
      snapshot: committed.snapshot,
      lease,
      isCurrentOwner,
      isPureCurrentOwner,
      snapshotMatches: () => {
        const entry = entryForDraft(
          committed.snapshot,
          nextAggregate.draftId,
        );
        return Boolean(
          entry &&
            appointmentWaitlistTerminalAttemptAggregatesAreExact(
              entry.draft,
              nextAggregate,
            ),
        );
      },
    });
    if (!postWrite.ownerCurrent || !postWrite.canonicalExact) {
      if (!postWrite.ownerCurrent && postWrite.canonicalExact) {
        compensatePostWriteCanonicalPublication({
          snapshot: committed.snapshot,
          staleEntries,
          lease,
        });
      }
      return { status: "refused" };
    }
    return {
      status:
        committed.status === "committed"
          ? "transitioned"
          : "already-transitioned",
      aggregate: nextAggregate,
      ownerCurrent: true,
    };
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}

export type CommitAppointmentWaitlistTerminalReceiptStrictResult =
  | {
      status: "committed" | "already-committed";
      aggregate: AppointmentWaitlistTerminalAttemptAggregateV1;
      receipt: AppointmentWaitlistReceiptV3;
      ownerCurrent: true;
    }
  | { status: "refused" };

export function commitAppointmentWaitlistTerminalReceiptStrict(input: {
  attempt: unknown;
  receipt: unknown;
  originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  expectedGatewayLineage: unknown;
  evaluatedAtEpochMs: number;
  isCurrentOwner: () => boolean;
  isPureCurrentOwner: () => boolean;
}): CommitAppointmentWaitlistTerminalReceiptStrictResult {
  let attempt: AppointmentWaitlistSubmissionAttemptV1 | null;
  let receipt: AppointmentWaitlistReceiptV3 | null;
  let originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  let expectedGatewayLineage: unknown;
  let evaluatedAtEpochMs: number;
  let isCurrentOwner: () => boolean;
  let isPureCurrentOwner: () => boolean;
  try {
    evaluatedAtEpochMs = input.evaluatedAtEpochMs;
    attempt = cloneAppointmentWaitlistTerminalAttempt(
      input.attempt,
      evaluatedAtEpochMs,
    );
    const receiptValidation = validateAppointmentWaitlistReceipt(input.receipt);
    receipt = receiptValidation.valid ? receiptValidation.receipt : null;
    originRequirement = input.originRequirement;
    expectedGatewayLineage = input.expectedGatewayLineage;
    isCurrentOwner = input.isCurrentOwner;
    isPureCurrentOwner = input.isPureCurrentOwner;
  } catch {
    return { status: "refused" };
  }
  if (
    !attempt ||
    attempt.state.kind !== "accepted" ||
    !receipt ||
    !appointmentWaitlistReceiptMatchesAcceptedAttempt({ attempt, receipt }) ||
    !isOriginRequirement(originRequirement) ||
    typeof isCurrentOwner !== "function" ||
    typeof isPureCurrentOwner !== "function"
  ) {
    return { status: "refused" };
  }
  const initial = captureCanonicalAggregateRawStable(evaluatedAtEpochMs);
  if (!initial) return { status: "refused" };
  const initialEntry = entryForDraft(initial, attempt.draftId);
  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify(["waitlist-terminal-receipt-commit", attempt.draftId]),
    isCurrentOwner,
  );
  if (!lease) return { status: "refused" };
  try {
    if (
      !initialEntry ||
      !aggregateMatchesAttempt({
        aggregate: initialEntry.draft,
        attempt,
        originRequirement,
        expectedGatewayLineage,
      })
    ) {
      return { status: "refused" };
    }
    if (initialEntry.draft.receipt !== null) {
      return appointmentWaitlistTerminalAttemptAggregatesAreExact(
        initialEntry.draft.receipt,
        receipt,
      ) &&
        ownerAndCanonicalSnapshotRemainExact({
          snapshot: initial,
          lease,
          isCurrentOwner,
          isPureCurrentOwner,
        }) &&
        finalSnapshotEntryIsExact({
          snapshot: initial,
          expected: initialEntry.draft,
          lease,
          isPureCurrentOwner,
        })
        ? {
            status: "already-committed",
            aggregate: initialEntry.draft,
            receipt: initialEntry.draft.receipt,
            ownerCurrent: true,
          }
        : { status: "refused" };
    }
    if (
      !ownerAndCanonicalSnapshotRemainExact({
        snapshot: initial,
        lease,
        isCurrentOwner,
        isPureCurrentOwner,
      })
    ) {
      return { status: "refused" };
    }
    const next: AppointmentWaitlistTerminalAttemptAggregateV1 = {
      ...initialEntry.draft,
      revision: initialEntry.draft.revision + 1,
      receipt,
    };
    const staleEntries = initial.entries.filter(
      (entry) => entry.draftId !== next.draftId,
    );
    const committed = commitCanonicalAggregateEntries({
      snapshot: initial,
      nextEntries: initial.entries.map((entry) =>
        entry.draftId === next.draftId
          ? { draftId: next.draftId, savedAtEpochMs: evaluatedAtEpochMs, draft: next }
          : entry,
      ),
      staleEntries,
      lease,
    });
    if (committed.status === "failed") {
      return { status: "refused" };
    }
    const postWrite = closePostWriteCanonicalAuthority({
      snapshot: committed.snapshot,
      lease,
      isCurrentOwner,
      isPureCurrentOwner,
      snapshotMatches: () => {
        const entry = entryForDraft(committed.snapshot, next.draftId);
        return Boolean(
          entry &&
            appointmentWaitlistTerminalAttemptAggregatesAreExact(
              entry.draft,
              next,
            ),
        );
      },
    });
    if (!postWrite.ownerCurrent || !postWrite.canonicalExact) {
      if (!postWrite.ownerCurrent && postWrite.canonicalExact) {
        compensatePostWriteCanonicalPublication({
          snapshot: committed.snapshot,
          staleEntries,
          lease,
        });
      }
      return { status: "refused" };
    }
    return {
      status:
        committed.status === "committed" ? "committed" : "already-committed",
      aggregate: next,
      receipt,
      ownerCurrent: true,
    };
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}

export function acknowledgeAppointmentWaitlistTerminalReceiptStrict(input: {
  receipt: unknown;
  originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  expectedGatewayLineage: unknown;
  acknowledgedAtEpochMs: number;
  evaluatedAtEpochMs: number;
  isCurrentOwner: () => boolean;
  isPureCurrentOwner: () => boolean;
}):
  | {
      status: "acknowledged" | "already-acknowledged";
      aggregate: AppointmentWaitlistTerminalAttemptAggregateV1;
    }
  | { status: "refused" } {
  let receipt: AppointmentWaitlistReceiptV3 | null;
  let originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  let expectedGatewayLineage: unknown;
  let acknowledgedAtEpochMs: number;
  let evaluatedAtEpochMs: number;
  let isCurrentOwner: () => boolean;
  let isPureCurrentOwner: () => boolean;
  try {
    const validation = validateAppointmentWaitlistReceipt(input.receipt);
    receipt = validation.valid ? validation.receipt : null;
    originRequirement = input.originRequirement;
    expectedGatewayLineage = input.expectedGatewayLineage;
    acknowledgedAtEpochMs = input.acknowledgedAtEpochMs;
    evaluatedAtEpochMs = input.evaluatedAtEpochMs;
    isCurrentOwner = input.isCurrentOwner;
    isPureCurrentOwner = input.isPureCurrentOwner;
  } catch {
    return { status: "refused" };
  }
  if (
    !receipt ||
    !isOriginRequirement(originRequirement) ||
    !isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(
      acknowledgedAtEpochMs,
    ) ||
    !isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(evaluatedAtEpochMs) ||
    acknowledgedAtEpochMs < receipt.submission.acceptedAtEpochMs ||
    acknowledgedAtEpochMs > evaluatedAtEpochMs + 5 * 60_000 ||
    typeof isCurrentOwner !== "function" ||
    typeof isPureCurrentOwner !== "function"
  ) {
    return { status: "refused" };
  }
  const initial = captureCanonicalAggregateRawStable(evaluatedAtEpochMs);
  if (!initial) return { status: "refused" };
  const initialEntry = entryForDraft(initial, receipt.draftId);
  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify(["waitlist-terminal-receipt-ack", receipt.draftId]),
    isCurrentOwner,
  );
  if (!lease) return { status: "refused" };
  try {
    if (
      !initialEntry ||
      initialEntry.draft.originRequirement !== originRequirement ||
      (originRequirement === "legacy-non-gateway"
        ? expectedGatewayLineage !== null ||
          initialEntry.draft.gatewayLineage !== null
        : !appointmentWaitlistGatewayLineagesAreExact(
            initialEntry.draft.gatewayLineage,
            expectedGatewayLineage,
          )) ||
      !initialEntry.draft.receipt ||
      !appointmentWaitlistTerminalAttemptAggregatesAreExact(
        initialEntry.draft.receipt,
        receipt,
      ) ||
      !ownerAndCanonicalSnapshotRemainExact({
        snapshot: initial,
        lease,
        isCurrentOwner,
        isPureCurrentOwner,
      })
    ) {
      return { status: "refused" };
    }
    if (initialEntry.draft.acknowledgedAtEpochMs !== null) {
      return finalSnapshotEntryIsExact({
        snapshot: initial,
        expected: initialEntry.draft,
        lease,
        isPureCurrentOwner,
      })
        ? {
            status: "already-acknowledged",
            aggregate: initialEntry.draft,
          }
        : { status: "refused" };
    }
    const next: AppointmentWaitlistTerminalAttemptAggregateV1 = {
      ...initialEntry.draft,
      revision: initialEntry.draft.revision + 1,
      acknowledgedAtEpochMs,
    };
    const staleEntries = initial.entries.filter(
      (entry) => entry.draftId !== next.draftId,
    );
    const committed = commitCanonicalAggregateEntries({
      snapshot: initial,
      nextEntries: initial.entries.map((entry) =>
        entry.draftId === next.draftId
          ? { draftId: next.draftId, savedAtEpochMs: evaluatedAtEpochMs, draft: next }
          : entry,
      ),
      staleEntries,
      lease,
    });
    if (committed.status === "failed") {
      return { status: "refused" };
    }
    const postWrite = closePostWriteCanonicalAuthority({
      snapshot: committed.snapshot,
      lease,
      isCurrentOwner,
      isPureCurrentOwner,
      snapshotMatches: () => {
        const entry = entryForDraft(committed.snapshot, next.draftId);
        return Boolean(
          entry &&
            appointmentWaitlistTerminalAttemptAggregatesAreExact(
              entry.draft,
              next,
            ),
        );
      },
    });
    if (!postWrite.ownerCurrent || !postWrite.canonicalExact) {
      if (!postWrite.ownerCurrent && postWrite.canonicalExact) {
        compensatePostWriteCanonicalPublication({
          snapshot: committed.snapshot,
          staleEntries,
          lease,
        });
      }
      return { status: "refused" };
    }
    return {
      status:
        committed.status === "committed"
          ? "acknowledged"
          : "already-acknowledged",
      aggregate: next,
    };
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}

export function retireAppointmentWaitlistTerminalAttemptAggregateStrict(input: {
  attempt: unknown;
  originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  expectedGatewayLineage: unknown;
  evaluatedAtEpochMs: number;
  isCurrentOwner: () => boolean;
  isPureCurrentOwner: () => boolean;
}): { status: "retired" | "already-retired" } | { status: "refused" } {
  let attempt: AppointmentWaitlistSubmissionAttemptV1 | null;
  let originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  let expectedGatewayLineage: unknown;
  let evaluatedAtEpochMs: number;
  let isCurrentOwner: () => boolean;
  let isPureCurrentOwner: () => boolean;
  try {
    evaluatedAtEpochMs = input.evaluatedAtEpochMs;
    attempt = cloneAppointmentWaitlistTerminalAttempt(
      input.attempt,
      evaluatedAtEpochMs,
    );
    originRequirement = input.originRequirement;
    expectedGatewayLineage = input.expectedGatewayLineage;
    isCurrentOwner = input.isCurrentOwner;
    isPureCurrentOwner = input.isPureCurrentOwner;
  } catch {
    return { status: "refused" };
  }
  if (
    !attempt ||
    attempt.state.kind !== "not-applied" ||
    !isOriginRequirement(originRequirement) ||
    typeof isCurrentOwner !== "function" ||
    typeof isPureCurrentOwner !== "function"
  ) {
    return { status: "refused" };
  }
  const initial = captureCanonicalAggregateRawStable(evaluatedAtEpochMs);
  if (!initial) return { status: "refused" };
  const initialEntry = entryForDraft(initial, attempt.draftId);
  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify(["waitlist-terminal-attempt-retire", attempt.draftId]),
    isCurrentOwner,
  );
  if (!lease) return { status: "refused" };
  try {
    if (!initialEntry) {
      return ownerAndCanonicalSnapshotRemainExact({
        snapshot: initial,
        lease,
        isCurrentOwner,
        isPureCurrentOwner,
      })
        ? { status: "already-retired" }
        : { status: "refused" };
    }
    if (
      initialEntry.draft.receipt !== null ||
      initialEntry.draft.acknowledgedAtEpochMs !== null ||
      !aggregateMatchesAttempt({
        aggregate: initialEntry.draft,
        attempt,
        originRequirement,
        expectedGatewayLineage,
      }) ||
      !ownerAndCanonicalSnapshotRemainExact({
        snapshot: initial,
        lease,
        isCurrentOwner,
        isPureCurrentOwner,
      })
    ) {
      return { status: "refused" };
    }
    const retained = initial.entries.filter(
      (entry) => entry.draftId !== attempt.draftId,
    );
    const committed = commitCanonicalAggregateEntries({
      snapshot: initial,
      nextEntries: retained,
      staleEntries: retained,
      lease,
    });
    if (committed.status === "failed") {
      return { status: "refused" };
    }
    const postWrite = closePostWriteCanonicalAuthority({
      snapshot: committed.snapshot,
      lease,
      isCurrentOwner,
      isPureCurrentOwner,
      snapshotMatches: () =>
        entryForDraft(committed.snapshot, attempt.draftId) === null,
    });
    if (!postWrite.ownerCurrent || !postWrite.canonicalExact) {
      // Retirement never resurrects an unacknowledged target. Re-publishing
      // the exact retained rows only proves that no unrelated winner was
      // overwritten while the owner changed at the public remove frontier.
      if (!postWrite.ownerCurrent && postWrite.canonicalExact) {
        compensatePostWriteCanonicalPublication({
          snapshot: committed.snapshot,
          staleEntries: retained,
          lease,
        });
      }
      return { status: "refused" };
    }
    return {
      status:
        committed.status === "committed" ? "retired" : "already-retired",
    };
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}

/**
 * Auth teardown may remove only resolved rows owned by the exact prior
 * customer. Pending/unknown attempts remain immutable reconciliation evidence,
 * and foreign-owner rows are preserved byte-for-byte.
 */
export function purgeResolvedAppointmentWaitlistTerminalAttemptAggregatesForOwner(
  customerAccountId: string | null,
) {
  if (customerAccountId !== null && !isIdentifier(customerAccountId)) {
    return false;
  }
  const mutationProof = captureCustomerOwnedSessionStorageMutationProof();
  try {
    const storage = window.sessionStorage;
    const initialRaw = readCustomerOwnedSessionStorageRawValueStable({
      storage,
      storageKey:
        APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_KEY,
    });
    if (
      initialRaw.status !== "ready" ||
      !customerOwnedSessionStorageMutationProofIsCurrent(mutationProof)
    ) {
      return false;
    }
    // The raw authority is established before the external clock callback.
    // After that callback only the already-captured canonical reader is used.
    const evaluatedAtEpochMs =
      readSafeAppointmentWaitlistTerminalAttemptEpoch();
    if (evaluatedAtEpochMs === null) return false;
    const postClockRaw =
      readCustomerOwnedSessionStorageRawValueFromCapturedReader({
        storage,
        storageKey:
          APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_KEY,
      });
    if (
      postClockRaw.status !== "ready" ||
      postClockRaw.rawValue !== initialRaw.rawValue ||
      !customerOwnedSessionStorageMutationProofIsCurrent(mutationProof)
    ) {
      return false;
    }
    const entries = parsePersistedAggregateEntries(
      postClockRaw.rawValue,
      evaluatedAtEpochMs,
    );
    if (!entries) return false;
    const retained =
      customerAccountId === null
        ? entries
        : entries.filter(
            ({ draft }) =>
              draft.customerAccountId !== customerAccountId ||
              draft.attempt.state.kind === "pending-reconciliation",
          );
    const nextRawValue = serializePersistedAggregateEntries(retained);
    let mutationStatus: "committed" | "superseded" | null = null;
    if (retained.length !== entries.length) {
      const mutation = commitCustomerOwnedSessionStorageMutation({
        storage,
        storageKey:
          APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_KEY,
        nextRawValue,
        expectedCurrentRawValue: postClockRaw.rawValue,
        mutationProof,
        staleCleanupRawValue: nextRawValue,
      });
      if (mutation.status !== "committed" && mutation.status !== "superseded") {
        return false;
      }
      mutationStatus = mutation.status;
    }
    const finalRaw = readCustomerOwnedSessionStorageRawValueFromCapturedReader({
      storage,
      storageKey:
        APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_KEY,
    });
    if (finalRaw.status !== "ready") return false;
    const finalEntries = parsePersistedAggregateEntries(
      finalRaw.rawValue,
      evaluatedAtEpochMs,
    );
    const retainedRowsSurvive = retained.every((expected) =>
      finalEntries?.some(
        (candidate) =>
          candidate.draftId === expected.draftId &&
          candidate.savedAtEpochMs === expected.savedAtEpochMs &&
          appointmentWaitlistTerminalAttemptAggregatesAreExact(
            candidate.draft,
            expected.draft,
          ),
      ),
    );
    return Boolean(
      finalEntries &&
        retainedRowsSurvive &&
        finalEntries.every(
          ({ draft }) =>
            customerAccountId === null ||
            draft.customerAccountId !== customerAccountId ||
            draft.attempt.state.kind === "pending-reconciliation",
        ) &&
        (mutationStatus === "committed"
          ? finalRaw.rawValue === nextRawValue
          : mutationStatus === "superseded"
            ? true
            : customerOwnedSessionStorageMutationProofIsCurrent(
                mutationProof,
              ) && finalRaw.rawValue === postClockRaw.rawValue),
    );
  } catch {
    return false;
  }
}

export type ReadAppointmentWaitlistTerminalAttemptAggregateStrictResult =
  | {
      status: "ready";
      aggregate: AppointmentWaitlistTerminalAttemptAggregateV1;
      attempt: AppointmentWaitlistSubmissionAttemptV1;
      resultsEntryProof: AppointmentWaitlistTerminalAttemptAggregateV1["resultsEntryProof"];
      originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1 | null;
      receipt: AppointmentWaitlistReceiptV3 | null;
    }
  | { status: "absent" | "owner-mismatch" | "unreadable" };

export function readAppointmentWaitlistTerminalAttemptAggregateStrict(input: {
  draftId: string;
  expectedCustomerAccountId: string;
  evaluatedAtEpochMs: number;
}): ReadAppointmentWaitlistTerminalAttemptAggregateStrictResult {
  let draftId: string;
  let expectedCustomerAccountId: string;
  let evaluatedAtEpochMs: number;
  try {
    draftId = input.draftId;
    expectedCustomerAccountId = input.expectedCustomerAccountId;
    evaluatedAtEpochMs = input.evaluatedAtEpochMs;
  } catch {
    return { status: "unreadable" };
  }
  if (
    !isIdentifier(draftId) ||
    !isIdentifier(expectedCustomerAccountId) ||
    !isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(evaluatedAtEpochMs)
  ) {
    return { status: "owner-mismatch" };
  }
  const stable = captureCanonicalAggregateRawStable(evaluatedAtEpochMs);
  if (!stable) return { status: "unreadable" };
  const entry = entryForDraft(stable, draftId);
  if (!entry) return { status: "absent" };
  if (entry.draft.customerAccountId !== expectedCustomerAccountId) {
    return { status: "owner-mismatch" };
  }
  return {
    status: "ready",
    aggregate: entry.draft,
    attempt: entry.draft.attempt,
    resultsEntryProof: entry.draft.resultsEntryProof,
    originRequirement: entry.draft.originRequirement,
    lineage: entry.draft.gatewayLineage,
    receipt: entry.draft.receipt,
  };
}

export function readAppointmentWaitlistTerminalReceiptStrict(input: {
  draftId: string;
  expectedCustomerAccountId: string;
  evaluatedAtEpochMs: number;
}):
  | {
      status: "ready";
      receipt: AppointmentWaitlistReceiptV3;
      attempt: AppointmentWaitlistSubmissionAttemptV1;
      originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
      lineage: SpecialistLocationGatewayCommittedNavigationLineageV1 | null;
    }
  | { status: "absent" | "owner-mismatch" | "unreadable" } {
  const aggregate =
    readAppointmentWaitlistTerminalAttemptAggregateStrict(input);
  if (aggregate.status !== "ready") return aggregate;
  if (!aggregate.receipt) return { status: "absent" };
  return {
    status: "ready",
    receipt: aggregate.receipt,
    attempt: aggregate.attempt,
    originRequirement: aggregate.originRequirement,
    lineage: aggregate.lineage,
  };
}

export function proveAppointmentWaitlistTerminalAttemptAggregateCurrentStrict(input: {
  attempt: unknown;
  originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  expectedGatewayLineage: unknown;
  expectedReceipt: unknown;
  requireAcknowledgedReceipt?: boolean;
  evaluatedAtEpochMs: number;
  isCurrentOwner: () => boolean;
  isPureCurrentOwner: () => boolean;
}):
  | {
      status: "ready";
      aggregate: AppointmentWaitlistTerminalAttemptAggregateV1;
    }
  | { status: "refused" } {
  let attempt: AppointmentWaitlistSubmissionAttemptV1 | null;
  let receipt: AppointmentWaitlistReceiptV3 | null;
  let originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  let expectedGatewayLineage: unknown;
  let requireAcknowledgedReceipt: boolean;
  let evaluatedAtEpochMs: number;
  let isCurrentOwner: () => boolean;
  let isPureCurrentOwner: () => boolean;
  try {
    evaluatedAtEpochMs = input.evaluatedAtEpochMs;
    attempt = cloneAppointmentWaitlistTerminalAttempt(
      input.attempt,
      evaluatedAtEpochMs,
    );
    if (input.expectedReceipt === null) {
      receipt = null;
    } else {
      const validation = validateAppointmentWaitlistReceipt(
        input.expectedReceipt,
      );
      if (!validation.valid) return { status: "refused" };
      receipt = validation.receipt;
    }
    originRequirement = input.originRequirement;
    expectedGatewayLineage = input.expectedGatewayLineage;
    requireAcknowledgedReceipt = input.requireAcknowledgedReceipt === true;
    isCurrentOwner = input.isCurrentOwner;
    isPureCurrentOwner = input.isPureCurrentOwner;
  } catch {
    return { status: "refused" };
  }
  if (
    !attempt ||
    !isOriginRequirement(originRequirement) ||
    !isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(evaluatedAtEpochMs) ||
    typeof isCurrentOwner !== "function" ||
    typeof isPureCurrentOwner !== "function" ||
    (requireAcknowledgedReceipt && receipt === null)
  ) {
    return { status: "refused" };
  }
  const initial = captureCanonicalAggregateRawStable(evaluatedAtEpochMs);
  if (!initial) return { status: "refused" };
  const initialEntry = entryForDraft(initial, attempt.draftId);
  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify(["waitlist-terminal-attempt-proof", attempt.draftId]),
    isCurrentOwner,
  );
  if (!lease) return { status: "refused" };
  try {
    if (
      !initialEntry ||
      !aggregateMatchesAttempt({
        aggregate: initialEntry.draft,
        attempt,
        originRequirement,
        expectedGatewayLineage,
      }) ||
      (receipt === null
        ? initialEntry.draft.receipt !== null
        : !initialEntry.draft.receipt ||
          !appointmentWaitlistTerminalAttemptAggregatesAreExact(
            initialEntry.draft.receipt,
            receipt,
          )) ||
      (requireAcknowledgedReceipt &&
        initialEntry.draft.acknowledgedAtEpochMs === null) ||
      !ownerAndCanonicalSnapshotRemainExact({
        snapshot: initial,
        lease,
        isCurrentOwner,
        isPureCurrentOwner,
      }) ||
      !finalSnapshotEntryIsExact({
        snapshot: initial,
        expected: initialEntry.draft,
        lease,
        isPureCurrentOwner,
      })
    ) {
      return { status: "refused" };
    }
    return { status: "ready", aggregate: initialEntry.draft };
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}

export function proveAppointmentWaitlistTerminalAttemptAggregateAbsentStrict(input: {
  draftId: string;
  expectedCustomerAccountId: string;
  evaluatedAtEpochMs: number;
  isCurrentOwner: () => boolean;
  isPureCurrentOwner: () => boolean;
}): { status: "absent" } | { status: "refused" } {
  let draftId: string;
  let expectedCustomerAccountId: string;
  let evaluatedAtEpochMs: number;
  let isCurrentOwner: () => boolean;
  let isPureCurrentOwner: () => boolean;
  try {
    draftId = input.draftId;
    expectedCustomerAccountId = input.expectedCustomerAccountId;
    evaluatedAtEpochMs = input.evaluatedAtEpochMs;
    isCurrentOwner = input.isCurrentOwner;
    isPureCurrentOwner = input.isPureCurrentOwner;
  } catch {
    return { status: "refused" };
  }
  if (
    !isIdentifier(draftId) ||
    !isIdentifier(expectedCustomerAccountId) ||
    !isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(evaluatedAtEpochMs) ||
    typeof isCurrentOwner !== "function" ||
    typeof isPureCurrentOwner !== "function"
  ) {
    return { status: "refused" };
  }
  const initial = captureCanonicalAggregateRawStable(evaluatedAtEpochMs);
  if (!initial || entryForDraft(initial, draftId)) {
    return { status: "refused" };
  }
  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify([
      "waitlist-terminal-attempt-absence-proof",
      draftId,
      expectedCustomerAccountId,
    ]),
    isCurrentOwner,
  );
  if (!lease) return { status: "refused" };
  try {
    return ownerAndCanonicalSnapshotRemainExact({
      snapshot: initial,
      lease,
      isCurrentOwner,
      isPureCurrentOwner,
    }) &&
      entryForDraft(initial, draftId) === null
      ? { status: "absent" }
      : { status: "refused" };
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}

export type InvokeAppointmentWaitlistTerminalReceiptNavigationStrictResult<Result> =
  | {
      status: "invoked";
      navigationInvocation:
        | { status: "returned"; value: Result }
        | { status: "threw" };
    }
  | { status: "refused" };

export function invokeAppointmentWaitlistTerminalAttemptNavigationStrict<Result>(input: {
  attempt: unknown;
  originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  expectedGatewayLineage: unknown;
  evaluatedAtEpochMs: number;
  isCurrentOwner: () => boolean;
  isPureCurrentOwner: () => boolean;
  navigate: () => Result;
}): InvokeAppointmentWaitlistTerminalReceiptNavigationStrictResult<Result> {
  let attempt: AppointmentWaitlistSubmissionAttemptV1 | null;
  let originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  let expectedGatewayLineage: unknown;
  let evaluatedAtEpochMs: number;
  let isCurrentOwner: () => boolean;
  let isPureCurrentOwner: () => boolean;
  let navigate: () => Result;
  try {
    evaluatedAtEpochMs = input.evaluatedAtEpochMs;
    attempt = cloneAppointmentWaitlistTerminalAttempt(
      input.attempt,
      evaluatedAtEpochMs,
    );
    originRequirement = input.originRequirement;
    expectedGatewayLineage = input.expectedGatewayLineage;
    isCurrentOwner = input.isCurrentOwner;
    isPureCurrentOwner = input.isPureCurrentOwner;
    navigate = input.navigate;
  } catch {
    return { status: "refused" };
  }
  if (
    !attempt ||
    !isOriginRequirement(originRequirement) ||
    !isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(evaluatedAtEpochMs) ||
    typeof isCurrentOwner !== "function" ||
    typeof isPureCurrentOwner !== "function" ||
    typeof navigate !== "function"
  ) {
    return { status: "refused" };
  }
  const initial = captureCanonicalAggregateRawStable(evaluatedAtEpochMs);
  if (!initial) return { status: "refused" };
  const initialEntry = entryForDraft(initial, attempt.draftId);
  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify(["waitlist-terminal-attempt-navigation", attempt.draftId]),
    isCurrentOwner,
  );
  if (!lease) return { status: "refused" };
  try {
    if (
      !initialEntry ||
      initialEntry.draft.receipt !== null ||
      !aggregateMatchesAttempt({
        aggregate: initialEntry.draft,
        attempt,
        originRequirement,
        expectedGatewayLineage,
      }) ||
      !ownerAndCanonicalSnapshotRemainExact({
        snapshot: initial,
        lease,
        isCurrentOwner,
        isPureCurrentOwner,
      }) ||
      !finalSnapshotEntryIsExact({
        snapshot: initial,
        expected: initialEntry.draft,
        lease,
        isPureCurrentOwner,
      })
    ) {
      return { status: "refused" };
    }
    try {
      return {
        status: "invoked",
        navigationInvocation: { status: "returned", value: navigate() },
      };
    } catch {
      return {
        status: "invoked",
        navigationInvocation: { status: "threw" },
      };
    }
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}

/**
 * Navigation frontier for an acknowledged receipt. The stable canonical raw
 * snapshot is established before the final owner callback; the router is
 * invoked only after captured-reader, mutation-generation, terminal-lease,
 * handoff-generation, owner, Receipt, origin, and immutable lineage proofs.
 */
export function invokeAppointmentWaitlistTerminalReceiptNavigationStrict<Result>(input: {
  receipt: unknown;
  originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  expectedGatewayLineage: unknown;
  evaluatedAtEpochMs: number;
  isCurrentOwner: () => boolean;
  isPureCurrentOwner: () => boolean;
  navigate: () => Result;
}): InvokeAppointmentWaitlistTerminalReceiptNavigationStrictResult<Result> {
  let receipt: AppointmentWaitlistReceiptV3 | null;
  let originRequirement: AppointmentWaitlistTerminalAttemptOriginRequirement;
  let expectedGatewayLineage: unknown;
  let evaluatedAtEpochMs: number;
  let isCurrentOwner: () => boolean;
  let isPureCurrentOwner: () => boolean;
  let navigate: () => Result;
  try {
    const validation = validateAppointmentWaitlistReceipt(input.receipt);
    receipt = validation.valid ? validation.receipt : null;
    originRequirement = input.originRequirement;
    expectedGatewayLineage = input.expectedGatewayLineage;
    evaluatedAtEpochMs = input.evaluatedAtEpochMs;
    isCurrentOwner = input.isCurrentOwner;
    isPureCurrentOwner = input.isPureCurrentOwner;
    navigate = input.navigate;
  } catch {
    return { status: "refused" };
  }
  if (
    !receipt ||
    !isOriginRequirement(originRequirement) ||
    !isAppointmentWaitlistTerminalAttemptEvaluatedEpoch(evaluatedAtEpochMs) ||
    typeof isCurrentOwner !== "function" ||
    typeof isPureCurrentOwner !== "function" ||
    typeof navigate !== "function"
  ) {
    return { status: "refused" };
  }
  const initial = captureCanonicalAggregateRawStable(evaluatedAtEpochMs);
  if (!initial) return { status: "refused" };
  const initialEntry = entryForDraft(initial, receipt.draftId);
  const lease = beginSpecialistLocationGatewayTerminalWrite(
    JSON.stringify(["waitlist-terminal-receipt-navigation", receipt.draftId]),
    isCurrentOwner,
  );
  if (!lease) return { status: "refused" };
  try {
    if (
      !initialEntry ||
      initialEntry.draft.customerAccountId !== receipt.customerAccountId ||
      initialEntry.draft.originRequirement !== originRequirement ||
      initialEntry.draft.acknowledgedAtEpochMs === null ||
      !initialEntry.draft.receipt ||
      !appointmentWaitlistTerminalAttemptAggregatesAreExact(
        initialEntry.draft.receipt,
        receipt,
      ) ||
      (originRequirement === "legacy-non-gateway"
        ? expectedGatewayLineage !== null ||
          initialEntry.draft.gatewayLineage !== null
        : !appointmentWaitlistGatewayLineagesAreExact(
            initialEntry.draft.gatewayLineage,
            expectedGatewayLineage,
          )) ||
      !ownerAndCanonicalSnapshotRemainExact({
        snapshot: initial,
        lease,
        isCurrentOwner,
        isPureCurrentOwner,
      }) ||
      !finalSnapshotEntryIsExact({
        snapshot: initial,
        expected: initialEntry.draft,
        lease,
        isPureCurrentOwner,
      })
    ) {
      return { status: "refused" };
    }
    try {
      return {
        status: "invoked",
        navigationInvocation: { status: "returned", value: navigate() },
      };
    } catch {
      return {
        status: "invoked",
        navigationInvocation: { status: "threw" },
      };
    }
  } finally {
    completeSpecialistLocationGatewayTerminalWrite(lease);
  }
}
